
import { Component, Prop, Vue } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import DossierFeedbackRepositoryInterface, {
  FeedbackChatCommentInterface, DossierFeedbackChatCommentInterface,
} from '@/services/Dossier/feedback/interfaces';
import { ProfileMapInterface, ReviewCommentInterface } from '@/services/QC-V2/chat/interfaces';
import { ProfileRepositoryInterface } from '@/services/Users-profile/interfaces';


interface FilterItemInterface {
  text: string,
  value: string | boolean | undefined,
}

interface FilterInterface {
  text: string,
  value: string,
  icon: string,
  items: FilterItemInterface[],
  selected: FilterItemInterface,
}

@Component({
  mixins: [
    Themeable,
  ],
  inject: [
    'dossierFeedbackRepository',
    'profileRepository',
  ],
  sockets: {
    feedbackChatComments(msg: { comments: ReviewCommentInterface }): void {
      this.$emit('feedback-chat-comments', msg.comments);
    },
  },
})
export default class AddFeedbackChatCommentsDialog extends Vue {
  @Prop() private userId!: string;
  @Prop({ required: false }) private excludeIds: number[];

  private visible: boolean = true;
  private loading: boolean = true;

  private dossierFeedbackRepository!: DossierFeedbackRepositoryInterface;
  private profileRepository!: ProfileRepositoryInterface;

  private profilesMap: ProfileMapInterface = {};
  private comments!: DossierFeedbackChatCommentInterface[];
  private feedbackChatComments: FeedbackChatCommentInterface[] = [];
  private selectedChatComments: FeedbackChatCommentInterface[] = [];

  private filter: FilterInterface = {
    text: 'Date',
    value: 'dates',
    icon: 'mdi-calendar',
    items: [
      { text: 'LAST 3 MONTHS', value: 'last 3 months' },
      { text: 'LAST 6 MONTHS', value: 'last 6 months' },
    ],
    selected: { text: 'LAST 3 MONTHS', value: 'last 3 months' },
  };

  private beforeCreate(): void {
    this.$on(
      'feedback-chat-comments',
      (comments: ReviewCommentInterface[]) => this.handleChatCommentsMessage(comments),
    );
  }

  private async created(): Promise<void> {
    await this.applyFilter();
  }

  private async initialize(months: string[] = []): Promise<void> {
    this.loading = true;
    this.selectedChatComments = [];
    this.feedbackChatComments = [];
    delete this.comments;
    try {
      this.comments = await this.dossierFeedbackRepository.getChatComments({
        userId: this.userId,
        excludeIds: this.excludeIds,
        months,
      });

      if (!this.comments.length) {
        this.loading = false;
      }
    } catch (e) {
      this.$errorDialog.show(e?.response?.data?.message || e.message);
      this.destroyComponent();
    }
  }

  private addComments(): void {
    this.$emit('chat-comments-added', this.selectedChatComments, this.profilesMap);
    this.destroyComponent();
  }

  private async handleChatCommentsMessage(comments: ReviewCommentInterface[]): Promise<void> {
    if (!this.comments) {
      setTimeout(() => {
        this.handleChatCommentsMessage(comments);
      }, 200);
      return;
    }

    if (!this.comments.length) {
      return;
    }

    const ids: string[] = [];
    const chatComments: FeedbackChatCommentInterface[] = [];
    comments.forEach((comment) => {
      const index = this.comments.findIndex((c) => c.commentId === comment.id);
      if (index === -1) {
        return;
      }

      chatComments.push({
        chatId: this.comments[index].chatId,
        reviewId: this.comments[index].reviewId,
        commentId: comment.id,
        author: comment.author,
        text: comment.text,
        timestamp: comment.timestamp,
      });

      if (!ids.includes(comment.author)) {
        ids.push(comment.author);
      }
    });

    const profiles = await this.profileRepository.getBaseProfileDataByUsersIds(ids);
    profiles.forEach((profile) => {
      this.profilesMap[profile.userId] = profile;
    });

    this.feedbackChatComments = chatComments;
    this.loading = false;
  }

  private getCommentLabel(comment: FeedbackChatCommentInterface): string {
    return `${comment.chatId}
    - ${this.profilesMap[comment.author].firstName} ${this.profilesMap[comment.author].lastName}: ${comment.text}`;
  }

  private async applyFilter(): Promise<void> {
    const months = this.getDateFilterData();
    await this.initialize(months);
  }

  private getDateFilterData(): string[] | undefined {
    if (!this.filter.selected || !this.filter.selected.value) {
      return undefined;
    }

    const selected = this.filter.selected.value as string;
    const monthsCountMatch = selected.match(/\d+/);
    if (!monthsCountMatch) {
      this.$errorDialog.show('Invalid Range Selected!');
      return;
    }
    const monthsCount = Number(monthsCountMatch[0]);

    const endDate = new Date();
    endDate.setMonth(endDate.getMonth());

    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 1 - monthsCount);


    return this.getMonthsArray(startDate, endDate);
  }

  private getMonthsArray(start: Date, end: Date): string[] {
    let length = (end.getFullYear() - start.getFullYear()) * 12;
    length -= start.getMonth();
    length += end.getMonth();

    return Array.from({ length }).map((_, index) => {
      const date = new Date(start);
      date.setMonth(date.getMonth() + index + 1);
      return date.toISOString().substr(0, 7);
    });
  }

  private destroyComponent(): void {
    this.$emit('close');
  }
}
